<template>
  <v-layout row justify-center>
    <v-dialog v-model="show" max-width="800px" persistent>
      <v-form id="newSlipForm" ref="form">
        <v-card class="elevation-12">
          <!-- Header moderno com gradiente -->
          <v-card-title class="white--text pa-6" :style="{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }">
            <div class="d-flex align-center w-100">
              <v-icon large class="mr-3">receipt</v-icon>
              <div>
                <h2 class="mb-1">Novo Boleto de Cobrança</h2>
                <p class="mb-0 white--text" style="opacity: 0.9;">
                  Preencha os dados para gerar um novo boleto
                </p>
              </div>
            </div>
          </v-card-title>

          <v-card-text class="pa-6">
            <!-- Seção: Dados do Pagador -->
            <v-card outlined class="mb-6">
              <v-card-title class="pb-2">
                <v-icon color="primary" class="mr-2">account_circle</v-icon>
                Dados do Pagador
              </v-card-title>
              <v-card-text>
                <!-- Campo de busca para favoritos -->
                <div v-if="payersList.length > 5" class="d-flex align-center mb-3">
                  <v-text-field
                    v-model="favoriteSearch"
                    label="Buscar favorito"
                    prepend-inner-icon="search"
                    outlined
                    dense
                    clearable
                    placeholder="Digite o nome, email ou documento..."
                    class="flex-grow-1 mr-2"
                  ></v-text-field>
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="loadPayers"
                    :loading="loadingPayers"
                    title="Atualizar lista de favoritos"
                  >
                    <v-icon>refresh</v-icon>
                  </v-btn>
                </div>

                <!-- Favoritos -->
                <v-select
                  :key="'favorites-' + payersList.length + '-' + Date.now()"
                  v-model="selectedPayerId"
                  :items="filteredPayersList"
                  label="Selecionar Favorito"
                  item-text="name"
                  item-value="id"
                  :menu-props="{ bottom: true, maxHeight: 400 }"
                  prepend-inner-icon="favorite"
                  clearable
                  searchable
                  placeholder="Escolha um pagador favorito para preencher automaticamente"
                  :no-data-text="payersList.length === 0 ? 'Nenhum favorito cadastrado' : filteredPayersList.length === 0 ? 'Nenhum resultado encontrado' : 'Nenhum resultado encontrado'"
                  @change="loadPayerData"
                >
                  <template v-slot:item="{ item }">
                    <div class="d-flex align-center py-2 px-2 favorite-item">
                      <v-avatar size="28" color="primary" class="mr-3">
                        <span class="white--text font-weight-medium">{{ item.name.charAt(0).toUpperCase() }}</span>
                      </v-avatar>
                      <div class="flex-grow-1 d-flex align-center">
                        <span class="font-weight-medium text-body-2 mr-3">{{ item.name }}</span>
                        <span class="text-caption grey--text mr-3">{{ item.document_type }}: {{ formatDocument(item.document) }}</span>
                        <span class="text-caption grey--text">{{ item.email }}</span>
                      </div>
                      <v-icon small color="primary" class="ml-2 flex-shrink-0">favorite</v-icon>
                    </div>
                  </template>
                  
                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar size="20" color="primary" class="mr-2">
                        <span class="white--text font-weight-medium">{{ item ? item.name.charAt(0).toUpperCase() : '' }}</span>
                      </v-avatar>
                      <span class="font-weight-medium text-body-2">{{ item ? item.name : '' }}</span>
                    </div>
                  </template>
                </v-select>

                <!-- Indicador de status da lista -->
                <v-alert
                  v-if="payersList.length === 0 && !loadingPayers"
                  type="info"
                  outlined
                  dense
                  class="mt-2 mb-4"
                >
                  <v-icon left small>info</v-icon>
                  Nenhum favorito cadastrado. 
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="loadPayers"
                  >
                    <v-icon left small>refresh</v-icon>
                    Recarregar
                  </v-btn>
                </v-alert>

                <!-- Indicador de favorito selecionado -->
                <v-alert
                  v-if="selectedPayerId && payer && payer.name"
                  type="success"
                  outlined
                  dense
                  class="mt-2 mb-4"
                >
                  <v-icon left small>check_circle</v-icon>
                  Favorito selecionado: <strong>{{ payer.name }}</strong>
                  <v-btn
                    small
                    text
                    color="error"
                    class="ml-2"
                    @click="clearSelectedPayer"
                  >
                    <v-icon left small>clear</v-icon>
                    Limpar
                  </v-btn>
                </v-alert>

                <v-container grid-list-md class="pa-0">
                  <v-layout wrap>
                    <!-- Nome -->
                    <v-flex xs12>
                      <v-text-field
                        :rules="presenceRules"
                        label="Nome Completo*"
                        v-model="payer.name"
                        prepend-inner-icon="person"
                        outlined
                        dense
                        required
                      ></v-text-field>
                    </v-flex>

                    <!-- Email -->
                    <v-flex xs12>
                      <v-text-field
                        :rules="emailRules"
                        label="E-mail*"
                        v-model="payer.email"
                        prepend-inner-icon="email"
                        outlined
                        dense
                        type="email"
                        required
                      ></v-text-field>
                    </v-flex>

                    <!-- CPF/CNPJ -->
                    <v-flex xs12>
                      <v-layout>
                        <v-flex xs3>
                          <v-select
                            :rules="presenceRules"
                            :items="documentType"
                            v-model="payer.document_type"
                            label="Tipo*"
                            outlined
                            dense
                            prepend-inner-icon="badge"
                          ></v-select>
                        </v-flex>
                        <v-flex xs9>
                          <v-text-field
                            :rules="presenceRules"
                            :label="payer.document_type === 'CPF' ? 'CPF*' : 'CNPJ*'"
                            v-model="payer.document"
                            v-mask="payer.document_type === 'CPF' ? '###.###.###-##' : '##.###.###/####-##'"
                            prepend-inner-icon="credit_card"
                            outlined
                            dense
                            required
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-flex>

                    <!-- Endereço -->
                    <v-flex xs12>
                      <v-layout>
                        <v-flex xs10>
                          <v-text-field
                            :rules="presenceRules"
                            label="Endereço*"
                            v-model="payer.address_street"
                            prepend-inner-icon="location_on"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs2>
                          <v-text-field
                            :rules="presenceRules"
                            label="Nº*"
                            v-model="payer.address_number"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-flex>

                    <!-- Cidade e UF -->
                    <v-flex xs12>
                      <v-layout>
                        <v-flex xs10>
                          <v-text-field
                            :rules="presenceRules"
                            label="Cidade*"
                            v-model="payer.address_city"
                            prepend-inner-icon="location_city"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs2>
                          <v-text-field
                            :rules="presenceRules"
                            label="UF*"
                            v-model="payer.address_state"
                            v-mask="'AA'"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-flex>

                    <!-- Bairro e CEP -->
                    <v-flex xs12>
                      <v-layout>
                        <v-flex xs8>
                          <v-text-field
                            :rules="presenceRules"
                            label="Bairro*"
                            v-model="payer.address_neighborhood"
                            prepend-inner-icon="home"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs4>
                          <v-text-field
                            :rules="presenceRules"
                            label="CEP*"
                            v-model="payer.address_code"
                            v-mask="'#####-###'"
                            prepend-inner-icon="markunread_mailbox"
                            outlined
                            dense
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>

            <!-- Seção: Dados do Boleto -->
            <v-card outlined class="mb-6">
              <v-card-title class="pb-2">
                <v-icon color="success" class="mr-2">attach_money</v-icon>
                Dados do Boleto
              </v-card-title>
              <v-card-text>
                <v-container grid-list-md class="pa-0">
                  <v-layout wrap>
                    <!-- Valor -->
                    <v-flex xs12 md6>
                      <v-text-field
                        :rules="presenceRules"
                        label="Valor do Boleto*"
                        v-model="formattedValue"
                        prepend-inner-icon="payments"
                        outlined
                        dense
                        prefix="R$"
                        @input="formatCurrency"
                        @blur="formatCurrencyOnBlur"
                        required
                        placeholder="0,00"
                      ></v-text-field>
                    </v-flex>

                    <!-- Data de Vencimento -->
                    <v-flex xs12 md6>
                      <v-text-field
                        :value="formattedDueDate"
                        label="Data de Vencimento*"
                        readonly
                        outlined
                        dense
                        prepend-inner-icon="event"
                        :hint="dueDateHint"
                        persistent-hint
                        :color="dueDateColor"
                      ></v-text-field>
                    </v-flex>

                    <!-- Descrição -->
                    <v-flex xs12>
                      <v-textarea
                        label="Descrição (opcional)"
                        v-model="description"
                        hint="Esta descrição aparecerá nos dados do boleto para identificação"
                        rows="3"
                        auto-grow
                        persistent-hint
                        outlined
                        prepend-inner-icon="description"
                      ></v-textarea>
                    </v-flex>
                  </v-layout>
                </v-container>

                <!-- Seletor de Data -->
                <v-card outlined class="mt-4">
                  <v-card-title class="pb-2">
                    <v-icon color="primary" class="mr-2">calendar_today</v-icon>
                    Selecionar Data de Vencimento
                  </v-card-title>
                  <v-card-text>
                      <v-layout row wrap align-center class="mb-3">
                        <v-flex xs12 md6>
                          <v-chip 
                            v-if="daysUntilDue !== null" 
                            :color="dueDateColor"
                            text-color="white"
                            large
                          >
                            <v-icon left>{{ dueDateIcon }}</v-icon>
                            {{ dueDateStatus }}
                          </v-chip>
                        </v-flex>
                        <v-flex xs12 md6 class="text-right">
                          <v-btn
                            v-for="days in [7, 15, 30]"
                            :key="days"
                            small
                            outlined
                            color="primary"
                            class="mr-2"
                            @click="setDueDate(days)"
                          >
                            {{ days }} dias
                          </v-btn>
                        </v-flex>
                      </v-layout>
                      <v-date-picker
                        v-model="due_date"
                        locale="pt-br"
                        :landscape="false"
                        :reactive="true"
                        :min="todayDate"
                        color="primary"
                        full-width
                      >                      </v-date-picker>
                    </v-card-text>
                  </v-card>
              </v-card-text>
            </v-card>

            <!-- Seção: Opções Avançadas -->
            <v-card outlined class="mb-6">
              <v-card-title class="pb-2">
                <v-icon color="info" class="mr-2">settings</v-icon>
                Opções Avançadas
              </v-card-title>
              <v-card-text>
                <v-container grid-list-md class="pa-0">
                  <v-layout wrap>
                    <!-- Favoritos -->
                    <v-flex xs12>
                      <v-checkbox
                        v-model="favorite"
                        label="Adicionar aos Favoritos"
                        color="primary"
                      ></v-checkbox>
                    </v-flex>

                    <!-- Tipo de Boleto -->
                    <v-flex xs12>
                      <v-radio-group v-model="row" row @change="billKind">
                        <v-radio 
                          label="Boleto Único" 
                          value="normal"
                          color="primary"
                        ></v-radio>
                        <v-radio
                          label="Boleto Recorrente"
                          value="subscribe"
                          color="primary"
                        ></v-radio>
                        <v-radio 
                          label="Boleto Parcelado" 
                          value="slice"
                          color="primary"
                        ></v-radio>
                      </v-radio-group>
                    </v-flex>

                    <!-- Opções de Parcelamento -->
                    <v-flex v-show="slice" xs12>
                      <v-card outlined class="pa-4">
                        <v-card-title class="pb-2">
                          <v-icon color="warning" class="mr-2">credit_card</v-icon>
                          Configurações de Parcelamento
                        </v-card-title>
                        <v-layout wrap>
                          <v-flex xs6>
                            <v-text-field
                              :rules="presenceRules"
                              label="Quantidade de Parcelas"
                              v-model="slices"
                              type="number"
                              min="2"
                              max="12"
                              outlined
                              dense
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs6>
                            <v-text-field
                              :rules="presenceRules"
                              label="Taxa por parcela (%)"
                              v-model="fee"
                              type="number"
                              min="0"
                              step="0.01"
                              outlined
                              dense
                              suffix="%"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-card>
                    </v-flex>

                    <!-- Opções de Recorrência -->
                    <v-flex v-show="subscribe" xs12>
                      <v-card outlined class="pa-4">
                        <v-card-title class="pb-2">
                          <v-icon color="success" class="mr-2">repeat</v-icon>
                          Configurações de Recorrência
                        </v-card-title>
                        <v-layout wrap>
                          <v-flex xs4>
                            <v-text-field
                              :rules="presenceRules"
                              label="A cada"
                              v-model="rangeSize"
                              type="number"
                              min="1"
                              outlined
                              dense
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs8>
                            <v-select
                              :rules="presenceRules"
                              :items="rangeTypes"
                              item-text="name"
                              item-value="value"
                              v-model="rangeType"
                              label="Período*"
                              outlined
                              dense
                            ></v-select>
                          </v-flex>
                          <v-flex xs12>
                            <v-text-field
                              :value="formattedFinalDate"
                              label="Data do último vencimento"
                              readonly
                              outlined
                              dense
                              prepend-inner-icon="event_busy"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12>
                            <v-date-picker
                              v-model="final_date"
                              locale="pt-br"
                              :landscape="false"
                              :reactive="true"
                              :min="due_date"
                              color="primary"
                              full-width
                            ></v-date-picker>
                          </v-flex>
                        </v-layout>
                      </v-card>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>

            <small class="text-muted">*campos obrigatórios</small>
            
            <!-- Alertas de Validação -->
            <v-alert
              v-if="showValidationErrors && validationErrors.length > 0"
              type="error"
              dismissible
              @click:close="showValidationErrors = false"
              class="mt-4"
            >
              <div class="font-weight-bold mb-2">Erros de Validação:</div>
              <ul class="mb-0">
                <li v-for="(error, index) in validationErrors" :key="index">
                  {{ error }}
                </li>
              </ul>
            </v-alert>
          </v-card-text>

          <!-- Actions -->
          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn 
              outlined 
              color="grey darken-1" 
              @click="closeModal"
              :disabled="loadingButton"
            >
              <v-icon left>close</v-icon>
              Cancelar
            </v-btn>
            <v-btn
              color="primary"
              :loading="loadingButton"
              @click="submitNewSlip"
              large
            >
              <v-icon left>receipt</v-icon>
              {{ loadingButton ? 'Gerando...' : 'Gerar Boleto' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Modal de Sucesso -->
    <v-dialog v-model="dialog" persistent max-width="500">
      <v-card class="elevation-12">
        <v-card-title class="success white--text">
          <v-icon large class="mr-3">check_circle</v-icon>
          Boleto Gerado com Sucesso!
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="mb-4">Seu boleto foi emitido e enviado ao e-mail do destinatário.</p>
          <v-alert
            v-if="createdBill && createdBill.processing_message"
            type="info"
            outlined
            class="mb-4"
          >
            <v-icon left>info</v-icon>
            {{ createdBill.processing_message }}
          </v-alert>
          <v-alert
            v-else
            type="info"
            outlined
            class="mb-4"
          >
            <v-icon left>info</v-icon>
            O download do PDF ficará disponível assim que o registro do boleto estiver completo em alguns instantes.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            @click="dialog = false"
            large
          >
            <v-icon left>check</v-icon>
            Entendi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Erro -->
    <v-dialog v-model="dialogErrors" persistent max-width="500">
      <v-card class="elevation-12">
        <v-card-title class="error white--text">
          <v-icon large class="mr-3">error</v-icon>
          Erro ao Gerar Boleto
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="mb-2"><strong>{{ billErrors.mensagem }}</strong></p>
                      <p class="text-muted">{{ billErrors.desenvolvedor && billErrors.desenvolvedor.mensagem }}</p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-btn 
            color="error" 
            block 
            @click="dialogErrors = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import { EventBus } from "@/services/event-bus";

export default {
  name: "NewSlipForm",
  created() {
    this.loadPayers();
    this.$store.dispatch("system/fetchSystemConfiguration");
    EventBus.$on("show-new-slip-form", () => {
      this.show = true;
      // Recarregar favoritos quando o modal é aberto
      this.loadPayers();
    });
  },
  computed: {
    ...mapGetters({
      createdBill: "bills/bill",
      billErrors: "bills/billErrors",
      systemAppName: "system/appName",
      primaryColor: "system/primaryColor",
      secondaryColor: "system/secondaryColor",
    }),
    payersList() {
      var payers = this.$store.getters['account/accountPayers'];
      // Garantir que sempre retorne um array
      if (payers && payers.constructor === Array) {
        return payers;
      } else if (payers && typeof payers === 'object') {
        // Se for um objeto, tentar converter para array
        return Object.keys(payers).map(function(key) { return payers[key]; });
      }
      return [];
    },
    filteredPayersList() {
      if (!this.favoriteSearch || this.favoriteSearch.trim() === '') {
        return this.payersList;
      }
      
      var searchTerm = this.favoriteSearch.toLowerCase().trim();
      return this.payersList.filter(function(payer) {
        return (
          (payer.name && payer.name.toLowerCase().includes(searchTerm)) ||
          (payer.email && payer.email.toLowerCase().includes(searchTerm)) ||
          (payer.document && payer.document.toLowerCase().includes(searchTerm))
        );
      });
    },
    todayDate() {
      return new Date().toISOString().substring(0, 10);
    },
    formattedDueDate() {
      if (!this.due_date) return 'Selecione uma data';
      
      var date = new Date(this.due_date + 'T00:00:00');
      return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
    },
    daysUntilDue() {
      if (!this.due_date) return null;
      
      var today = new Date();
      var dueDate = new Date(this.due_date + 'T00:00:00');
      var diffTime = dueDate - today;
      var diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    },
    dueDateStatus() {
      if (this.daysUntilDue === null) return '';
      
      if (this.daysUntilDue < 0) {
        return (this.daysUntilDue < 0 ? -this.daysUntilDue : this.daysUntilDue) + ' dia(s) atrás';
      } else if (this.daysUntilDue === 0) {
        return 'Vence hoje';
      } else if (this.daysUntilDue === 1) {
        return 'Vence amanhã';
      } else {
        return this.daysUntilDue + ' dias';
      }
    },
    dueDateHint() {
      if (!this.due_date) return 'Selecione a data de vencimento do boleto';
      
      if (this.daysUntilDue < 0) {
        return '⚠️ Data no passado - Verifique se está correto';
      } else if (this.daysUntilDue <= 7) {
        return '⚡ Vencimento próximo';
      } else {
        return '✅ Data de vencimento válida';
      }
    },

    formattedFinalDate() {
      if (!this.final_date) return 'Selecione uma data';
      var date = new Date(this.final_date + 'T00:00:00');
      return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
    },

    dueDateColor() {
      if (this.daysUntilDue === null) return 'grey';
      if (this.daysUntilDue < 0) return 'error';
      if (this.daysUntilDue <= 7) return 'warning';
      return 'success';
    },
    dueDateIcon() {
      if (this.daysUntilDue === null) return 'help_outline';
      if (this.daysUntilDue < 0) return 'warning';
      if (this.daysUntilDue <= 7) return 'info';
      return 'check_circle';
    }
  },
  watch: {
    createdBill(newValue, oldValue) {
      this.dialog = true;
      this.show = false;
      this.loadingButton = false;
      
      // Atualizar a lista de boletos após criação (com delay para garantir que foi salvo)
      setTimeout(() => {
        this.refreshBillsList();
      }, 1000);
    },
    billErrors(newValue, oldValue) {
      this.dialogErrors = true;
      this.show = false;
      this.loadingButton = false;
    },
    // Limpar erros quando campos são alterados
    'payer.name'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.email'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.document'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.address_street'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.address_number'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.address_city'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    'payer.address_state'() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    due_date() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    value() {
      this.showValidationErrors = false;
      this.validationErrors = [];
    },
    // Limpar busca quando modal é fechado
    show(newValue) {
      if (!newValue) {
        this.favoriteSearch = "";
        this.selectedPayerId = null;
      } else {
        // Recarregar favoritos quando o modal é aberto
        this.loadPayers();
      }
    },
    // Monitorar mudanças na lista de favoritos
    payersList() {
      // Forçar reatividade quando a lista muda
      this.$forceUpdate();
    },
  },
  data: () => ({
    due_limit: new Date().toISOString().substring(0, 10),
    picker: new Date().toISOString().substring(0, 10),
    show: false,
    dialog: false,
    dialogErrors: false,
    loadingButton: false,
    downloadingPdf: false,
    validationErrors: [],
    showValidationErrors: false,
    selectedPayerId: null,
    favoriteSearch: "",
    loadingPayers: false,
    documentType: ["CPF", "CNPJ"],
    rangeTypes: [
      { name: "Dia", value: "daily" },
      { name: "Mês", value: "monthly" },
      { name: "Ano", value: "yearly" },
    ],
    rangeType: "monthly",
    rangeSize: 1,
    due_date: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10), // 7 days from now
    final_date: "",
    value: 0,
    formattedValue: "0,00",
    description: "",
    favorite: false,
    subscribe: false,
    slice: false,
    slices: "",
    fee: "",
    column: null,
    row: "normal",
    params: [],
    payer: {
      id: "",
      name: "",
      email: "",
      document_type: "CPF",
      document: "",
      address_street: "",
      address_number: "",
      address_neighborhood: "",
      address_city: "",
      address_state: "",
      address_code: "",
    },
    payers: [],
    presenceRules: [(v) => !!v || "Campo obrigatório"],
    emailRules: [
      (v) => !!v || "E-mail é obrigatório",
      (v) => /.+@.+\..+/.test(v) || "E-mail deve ser válido",
    ],
  }),
  methods: {
    submitNewSlip() {
      // Validar formulário primeiro
      if (!this.$refs.form) {
        this.$toast ? this.$toast.error('Erro no formulário') : console.error('Erro no formulário');
        return;
      }

      // Validação manual para campos críticos
      var errors = [];
      
      // Validação detalhada de cada campo
      if (!this.payer.name || this.payer.name.trim() === '') {
        errors.push('❌ Nome do pagador é obrigatório');
      }
      
      if (!this.payer.email || this.payer.email.trim() === '') {
        errors.push('❌ E-mail do pagador é obrigatório');
      } else if (!/.+@.+\..+/.test(this.payer.email)) {
        errors.push('❌ E-mail do pagador deve ser válido');
      }
      
      if (!this.payer.document || this.payer.document.trim() === '') {
        errors.push('❌ CPF/CNPJ do pagador é obrigatório');
      }
      
      if (!this.payer.address_street || this.payer.address_street.trim() === '') {
        errors.push('❌ Endereço do pagador é obrigatório');
      }
      
      if (!this.payer.address_number || this.payer.address_number.toString().trim() === '') {
        errors.push('❌ Número do endereço é obrigatório');
      }
      
      if (!this.payer.address_city || this.payer.address_city.trim() === '') {
        errors.push('❌ Cidade do pagador é obrigatória');
      }
      
      if (!this.payer.address_state || this.payer.address_state.trim() === '') {
        errors.push('❌ Estado do pagador é obrigatório');
      }
      
      if (!this.due_date) {
        errors.push('❌ Data de vencimento é obrigatória');
      }
      
      // Debug: verificar valores
      console.log('Validação - value:', this.value, 'formattedValue:', this.formattedValue);
      console.log('Validação - payer:', this.payer);
      console.log('Validação - due_date:', this.due_date);
      
      if (!this.value || this.value <= 0 || !this.formattedValue || this.formattedValue === '0,00') {
        errors.push('❌ Valor do boleto deve ser maior que zero');
      }

      // Se há erros, mostrar todos
      if (errors.length > 0) {
        console.log('Erros encontrados:', errors);
        var errorMessage = errors.join('\n');
        
        // Mostrar erros na interface
        this.validationErrors = errors;
        this.showValidationErrors = true;
        
        // Tentar diferentes formas de mostrar o erro
        if (this.$toast) {
          this.$toast.error(errorMessage);
        } else if (this.$vuetify && this.$vuetify.framework) {
          this.$vuetify.framework.goTo(0, { duration: 0 });
          alert(errorMessage);
        } else {
          alert(errorMessage);
        }
        
        console.error('Erros de validação:', errorMessage);
        return;
      }

      // Verificação final - se chegou até aqui, tudo está válido
      console.log('✅ Todos os campos estão válidos, prosseguindo com a geração do boleto');
      
      // O valor já está no formato correto (número)
      var numericValue = this.value;
      
      this.params = {
        value: numericValue,
        due_date: this.due_date,
        description: this.description,
        payer_name: this.payer.name,
        payer_email: this.payer.email,
        payer_document_type: this.payer.document_type,
        payer_document: this.payer.document,
        payer_address_street: this.payer.address_street,
        payer_address_number: this.payer.address_number.toString(),
        payer_address_neighborhood: this.payer.address_neighborhood,
        payer_address_city: this.payer.address_city,
        payer_address_state: this.payer.address_state,
        payer_address_code: this.payer.address_code,
        favorite: this.favorite,
        subscribe: this.subscribe,
        final_date: this.final_date,
        slice: this.slice,
        slices: this.slices,
        fee: this.fee,
        range_type: this.rangeType,
        range_size: this.rangeSize,
      };
      
      this.$store.dispatch("bills/slipRecharge", this.params);
      this.loadingButton = true;
    },
    downloadPdfBill() {
      this.downloadingPdf = true;
      
      try {
        if (this.createdBill && this.createdBill.constructor === Array) {
          this.createdBill.forEach(function(item, index) {
            if (item.pdfBinario) {
              var link = document.createElement("a");
              link.href = "data:application/pdf;base64," + item.pdfBinario;
              link.download = 'boleto_' + (this.systemAppName || 'flowit') + '_' + new Date().getTime() + '_' + (index + 1) + '.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          });
        } else if (this.createdBill && this.createdBill.pdfBinario) {
          // Caso seja um único boleto
          var link = document.createElement("a");
          link.href = "data:application/pdf;base64," + this.createdBill.pdfBinario;
          link.download = 'boleto_' + (this.systemAppName || 'flowit') + '_' + new Date().getTime() + '.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          this.$toast ? this.$toast.error('PDF não disponível para download') : console.error('PDF não disponível para download');
        }
      } catch (error) {
        console.error('Erro ao baixar PDF:', error);
        this.$toast ? this.$toast.error('Erro ao baixar o PDF') : console.error('Erro ao baixar o PDF');
      } finally {
        this.downloadingPdf = false;
      }
    },
    billKind() {
      if (this.row == "subscribe") {
        this.slice = false;
        this.subscribe = true;
      }

      if (this.row == "slice") {
        this.slice = true;
        this.subscribe = false;
      }

      if (this.row == "normal") {
        this.slice = false;
        this.subscribe = false;
      }
    },
    loadPayerData(selectedPayerId) {
      if (selectedPayerId) {
        // Encontrar o pagador selecionado na lista
        const selectedPayer = this.payersList.find(payer => payer.id === selectedPayerId);
        
        if (selectedPayer) {
          // Carregar dados do pagador selecionado
          this.payer = Object.assign({}, selectedPayer);
          
          // Aguardar o DOM atualizar e forçar validação
          this.$nextTick(() => {
            if (this.$refs.form) {
              this.$refs.form.validate();
            }
          });
        }
      }
    },
    setDueDate(days) {
      var newDate = new Date();
      newDate.setDate(newDate.getDate() + days);
      this.due_date = newDate.toISOString().substring(0, 10);
    },
      formatCurrency(value) {
        // Limpar erros de validação quando o usuário digita
        this.showValidationErrors = false;
        this.validationErrors = [];
        
        if (!value) {
          this.value = 0;
          this.formattedValue = '0,00';
          return;
        }
        
        // Remover todos os caracteres não numéricos
        var cleanValue = value.toString().replace(/[^\d]/g, '');
        
        if (!cleanValue) {
          this.value = 0;
          this.formattedValue = '0,00';
          return;
        }
        
        // Converter para centavos e depois para reais
        var valueInCents = parseInt(cleanValue, 10);
        var valueInReais = valueInCents / 100;
        
        // Atualizar o valor numérico
        this.value = valueInReais;
        
        // Formatar para exibição com vírgulas
        var formatted = valueInReais.toFixed(2);
        // Adicionar vírgulas para milhares
        var parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        this.formattedValue = parts.join(',').replace('.', ',');
      },
      formatCurrencyOnBlur() {
        // Garantir que o valor esteja bem formatado quando o campo perde o foco
        if (this.value && this.value > 0) {
          var formatted = this.value.toFixed(2);
          // Adicionar vírgulas para milhares
          var parts = formatted.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          this.formattedValue = parts.join(',').replace('.', ',');
        }
            },
      clearSelectedPayer() {
        this.selectedPayerId = null;
        this.payer = {
          id: "",
          name: "",
          email: "",
          document_type: "CPF",
          document: "",
          address_street: "",
          address_number: "",
          address_neighborhood: "",
          address_city: "",
          address_state: "",
          address_code: "",
        };
      },
      formatDocument(document) {
        if (!document) return '';
        
        // Remover caracteres não numéricos
        const clean = document.replace(/\D/g, '');
        
        // Formatar CPF (11 dígitos)
        if (clean.length === 11) {
          return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        
        // Formatar CNPJ (14 dígitos)
        if (clean.length === 14) {
          return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        
        // Retornar original se não conseguir formatar
        return document;
      },
      refreshBillsList() {
        // Recarregar a lista de boletos após criação
        if (this.$store && this.$store.dispatch) {
          // Usar a mesma ação que o BillsTable usa
          this.$store.dispatch('bills/loadBills', this.$store.getters['bills/billsFilter']);
        }
      },
      loadPayers() {
        // Recarregar a lista de favoritos
        if (this.$store && this.$store.dispatch) {
          this.loadingPayers = true;
          this.$store.dispatch("account/getAccountPayers").finally(() => {
            this.loadingPayers = false;
          });
        }
      },
      closeModal() {
        this.show = false;
        this.showValidationErrors = false;
        this.validationErrors = [];
        this.favoriteSearch = "";
        this.selectedPayerId = null;
        // Resetar formulário
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      this.payer = {
        id: "",
        name: "",
        email: "",
        document_type: "CPF",
        document: "",
        address_street: "",
        address_number: "",
        address_neighborhood: "",
        address_city: "",
        address_state: "",
        address_code: "",
      };
      this.value = 0;
      this.formattedValue = "0,00";
      this.description = "";
      this.favorite = false;
      this.row = "normal";
      this.slice = false;
      this.subscribe = false;
    }
  },
};
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.favorite-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
  margin-bottom: 4px;
}

.favorite-item:hover {
  background-color: #f8f9fa;
}

.favorite-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 4px;
}

.mb-2 {
  margin-bottom: 8px;
}

/* Melhorar layout horizontal dos favoritos */
.favorite-item .d-flex.align-center {
  white-space: nowrap;
  overflow: hidden;
}

.favorite-item .d-flex.align-center span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

/* Melhorar espaçamento do v-select */
.v-select__selections {
  padding: 8px 0;
}

.v-select__slot {
  margin-bottom: 8px;
}
</style>

<style scoped>
.v-card-title h2 {
  font-size: 1.5rem;
  font-weight: 600;
}

.v-card-title p {
  font-size: 0.9rem;
}

.text-muted {
  color: rgba(0, 0, 0, 0.6) !important;
}

.v-expansion-panels {
  border-radius: 8px;
}

.v-expansion-panel {
  border: 1px solid #e0e0e0;
}

.v-expansion-panel-header {
  background-color: #f5f5f5;
}

.v-chip {
  font-weight: 500;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-text-field--outlined {
  border-radius: 8px;
}

.v-textarea--outlined {
  border-radius: 8px;
}

.v-select--outlined {
  border-radius: 8px;
}

/* Melhorar aparência dos cards */
.v-card.v-card--outlined {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.v-card.v-card--outlined:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

/* Responsividade */
@media (max-width: 600px) {
  .v-dialog {
    margin: 16px;
  }
  
  .v-card-title {
    padding: 16px !important;
  }
  
  .v-card-text {
    padding: 16px !important;
  }
  
  .v-card-actions {
    padding: 16px !important;
  }
}
</style>
